import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Btn } from '../../components/Btn';
import { FilterBar } from '../../components/FilterBar';
import { Pill, type PillTone } from '../../components/Pill';
import { DataTable } from '../../components/Table';
import { listBranches } from '../../data/fetchers';
import { fmtDate } from '../../lib/format';
import { listTransfersPage, type TransferView } from '../../lib/transfers';
import { useBranchScope } from '../../lib/useBranchScope';
import { StockHeader } from './StockHeader';
import { StockPagination, useStockCursor } from './StockPagination';

const STATUS_TH: Record<TransferView['status'], string> = {
  draft: 'ร่าง',
  sent: 'ส่งแล้ว',
  received: 'รับแล้ว',
  cancelled: 'ยกเลิก',
};
const STATUS_TONE: Record<TransferView['status'], PillTone> = {
  draft: 'neutral',
  sent: 'primary',
  received: 'ok',
  cancelled: 'danger',
};
const TABS: { key: TransferView['status'] | 'all'; label: string }[] = [
  { key: 'all', label: 'ทั้งหมด' },
  { key: 'sent', label: 'ส่งแล้ว' },
  { key: 'received', label: 'รับแล้ว' },
  { key: 'draft', label: 'ร่าง' },
  { key: 'cancelled', label: 'ยกเลิก' },
];

interface TrRow {
  id: string;
  referenceNo: string;
  from: string;
  to: string;
  date: string;
  status: TransferView['status'];
  lines: number;
}

export function TransfersView() {
  const navigate = useNavigate();
  const { scope, ready } = useBranchScope();
  const [tab, setTab] = useState<TransferView['status'] | 'all'>('all');
  const pager = useStockCursor(JSON.stringify([scope, tab]));
  const transferQuery = useQuery({
    queryKey: [
      'transfers',
      scope.branchId,
      scope.allowedBranchIds,
      tab,
      pager.cursor,
    ],
    queryFn: () =>
      listTransfersPage(scope, {
        status: tab === 'all' ? undefined : tab,
        before: pager.cursor,
        limit: 25,
      }),
    enabled: ready,
  });
  const transfers = transferQuery.data?.transfers ?? [];

  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const shortOf = (id: string) =>
    branches.find((b) => b.id === id)?.short ?? id;

  const rows = useMemo<TrRow[]>(
    () =>
      transfers
        .filter((t) => tab === 'all' || t.status === tab)
        .map((t) => ({
          id: t.id,
          referenceNo: t.referenceNo,
          from: shortOf(t.fromBranchId),
          to: shortOf(t.toBranchId),
          date: fmtDate(t.createdAt),
          status: t.status,
          lines: t.lines.length,
        })),
    [transfers, branches, tab],
  );

  const columns: ColumnDef<TrRow, unknown>[] = [
    { accessorKey: 'referenceNo', header: 'เลขที่' },
    { accessorKey: 'from', header: 'จาก' },
    { accessorKey: 'to', header: 'ไป' },
    { accessorKey: 'date', header: 'วันที่' },
    {
      accessorKey: 'lines',
      header: 'รายการ',
      cell: (c) => <span className="num">{c.getValue<number>()}</span>,
    },
    {
      accessorKey: 'status',
      header: 'สถานะ',
      cell: (c) => {
        const s = c.getValue<TransferView['status']>();
        return <Pill tone={STATUS_TONE[s]}>{STATUS_TH[s]}</Pill>;
      },
    },
  ];

  return (
    <div className="bo-fade">
      <StockHeader
        title="โอนย้ายระหว่างสาขา"
        subtitle="สร้าง ติดตาม และรับสินค้าที่เคลื่อนย้ายระหว่างสาขา"
        status={{
          label: `${transfers.length} ใบโอนในหน้านี้`,
          tone: transfers.some((transfer) => transfer.status === 'sent')
            ? 'warn'
            : 'ok',
        }}
        actions={
          <Btn kind="primary" onClick={() => navigate('/transfers/new')}>
            + สร้างใบโอน
          </Btn>
        }
      />
      <FilterBar>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`min-h-11 px-3 rounded-btn text-sm-label ${tab === t.key ? 'bg-primary text-white font-bold' : 'bg-surface border border-line text-ink-2'}`}
          >
            {t.label}
          </button>
        ))}
      </FilterBar>
      <DataTable
        pageSize={25}
        columns={columns}
        data={rows}
        mobileCards
        onRowClick={(row) => navigate(`/transfers/${row.id}`)}
      />
      {transferQuery.isError && (
        <p role="alert">
          โหลดใบโอนไม่สำเร็จ{' '}
          <button onClick={() => void transferQuery.refetch()}>ลองใหม่</button>
        </p>
      )}
      <StockPagination
        pager={pager}
        hasMore={transferQuery.data?.hasMore}
        nextCursor={transferQuery.data?.nextCursor}
        pending={transferQuery.isFetching}
      />
    </div>
  );
}
